import { useEffect, useRef, useState } from 'react';

// Same yellow a selected table row carries (components/table/components/TableRow),
// held for a moment rather than for as long as something stays selected: after a
// save, the card that changed says so. It earns its keep on a panel long enough
// for the edited section to sit off screen.
export const FLASH_CLASS = 'bg-yellow-50 transition-colors duration-300';
export const FLASH_DURATION_MS = 2500;

/**
 * Marks the card a save came from and brings it into view.
 *
 * Register each card with `registerCard(key)` as its ref, colour it with
 * `isFlashed(key)`, and call `flashCard(key)` once the save has landed.
 */
const useCardFlash = () => {
  // A fresh object every time: saving the same card twice in a row must restart
  // the flash, and a bare key would compare equal and leave the effect asleep.
  const [flash, setFlash] = useState(null);
  const cardRefs = useRef({});

  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), FLASH_DURATION_MS);
    return () => clearTimeout(timer);
  }, [flash]);

  const flashCard = (key) => {
    setFlash({ key });
    // 'nearest' leaves an already visible card where it is: the panel only
    // moves when the edited card is off screen.
    cardRefs.current[key]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  return {
    flashCard,
    isFlashed: (key) => flash?.key === key,
    registerCard: (key) => (node) => {
      cardRefs.current[key] = node;
    },
  };
};

export default useCardFlash;
